import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as articleService from '../../services/article';
import * as imageService from '../../services/image';

// Async thunks
export const saveArticle = createAsyncThunk('articles/save', async (articleData, { rejectWithValue }) => {
    try {
        return await articleService.saveArticle(articleData);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const uploadMedia = createAsyncThunk('articles/uploadMedia', async (file, { rejectWithValue }) => {
    try {
        if (!file) {
            return rejectWithValue('No file provided');
        }

        // Check if it's an image
        if (file.type.startsWith('image/')) {
            const result = await imageService.uploadImage(file);
            return result;
        }

        // For other media types
        return await articleService.uploadMedia(file);
    } catch (error) {
        console.error('Upload error:', error);
        // Preserve the original error message for validation errors
        return rejectWithValue(error.message || 'Failed to upload media');
    }
});

export const fetchArticles = createAsyncThunk(
    'articles/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            return await articleService.fetchArticles();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    },
    {
        condition: (_, { getState }) => {
            const state = getState();
            // Prevent duplicate requests if already loading
            if (state.articles.status === 'loading') {
                return false;
            }
            return true;
        },
    }
);

export const fetchArticleById = createAsyncThunk('articles/fetchById', async (articleId, { rejectWithValue }) => {
    try {
        return await articleService.fetchArticleById(articleId);
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteArticle = createAsyncThunk('articles/delete', async (articleId, { rejectWithValue }) => {
    try {
        await articleService.deleteArticle(articleId);
        return articleId; // Return the deleted article ID
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const toggleArticleVisibility = createAsyncThunk('articles/toggleVisibility', async ({ articleId, isVisible }, { rejectWithValue }) => {
    try {
        const updatedArticle = await articleService.toggleArticleVisibility(articleId, isVisible);
        return { articleId, isVisible, updatedArticle };
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const initialState = {
    articles: [],
    currentArticle: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    uploadStatus: 'idle',
    uploadError: null,
    uploadProgress: 0,
    uploads: {}, // Track multiple uploads
};

const articleSlice = createSlice({
    name: 'articles',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
            state.uploadError = null;
        },
        resetStatus: (state) => {
            state.status = 'idle';
            state.uploadStatus = 'idle';
        },
        clearCurrentArticle: (state) => {
            state.currentArticle = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Save article
            .addCase(saveArticle.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(saveArticle.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Update or add the saved article in the articles array
                const index = state.articles.findIndex((article) => article.id === action.payload.id);
                if (index !== -1) {
                    state.articles[index] = action.payload;
                } else {
                    state.articles.push(action.payload);
                }
                state.currentArticle = action.payload;
            })
            .addCase(saveArticle.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Upload media
            .addCase(uploadMedia.pending, (state) => {
                state.uploadStatus = 'loading';
                state.uploadProgress = 0;
                state.uploadError = null;
            })
            .addCase(uploadMedia.fulfilled, (state, action) => {
                state.uploadStatus = 'succeeded';
                state.uploadProgress = 100;
                // Store the uploaded media info
                state.uploads[action.payload.id] = {
                    ...action.payload,
                    status: 'succeeded',
                };
            })
            .addCase(uploadMedia.rejected, (state, action) => {
                state.uploadStatus = 'failed';
                state.uploadError = action.payload;
                state.uploadProgress = 0;
            })
            // Fetch all articles
            .addCase(fetchArticles.pending, (state) => {
                state.status = 'loading';
                state.error = null; // Reset error on new fetch
            })
            .addCase(fetchArticles.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.articles = action.payload;
            })
            .addCase(fetchArticles.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Fetch article by id
            .addCase(fetchArticleById.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(fetchArticleById.fulfilled, (state, action) => {
                state.status = 'succeeded';
                state.currentArticle = action.payload;
            })
            .addCase(fetchArticleById.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            })
            // Delete article
            .addCase(deleteArticle.pending, (state) => {
                state.status = 'loading';
                state.error = null; // Reset error
            })
            .addCase(deleteArticle.fulfilled, (state, action) => {
                state.status = 'succeeded';
                // Remove the article from the state
                state.articles = state.articles.filter((article) => {
                    const articleId = article.id || article.idArticle || article.id_article;
                    return articleId !== action.payload;
                });
                // Clear current article if it was the one deleted
                if (state.currentArticle) {
                    const currentId = state.currentArticle.id || state.currentArticle.idArticle || state.currentArticle.id_article;
                    if (currentId === action.payload) {
                        state.currentArticle = null;
                    }
                }
                state.error = null; // Clear error on success
            })
            .addCase(deleteArticle.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.error.message;
            })
            // Toggle article visibility
            .addCase(toggleArticleVisibility.pending, (state) => {
                state.status = 'loading';
                state.error = null; // Reset error
            })
            .addCase(toggleArticleVisibility.fulfilled, (state, action) => {
                state.status = 'succeeded';
                const { articleId, isVisible } = action.payload;
                // Update the article in the articles array
                const article = state.articles.find((a) => {
                    const id = a.id || a.idArticle || a.id_article;
                    return id === articleId;
                });
                if (article) {
                    article.visible = isVisible;
                    article.isVisible = isVisible; // Handle both possible property names
                }
                // Update current article if it matches
                if (state.currentArticle) {
                    const currentId = state.currentArticle.id || state.currentArticle.idArticle || state.currentArticle.id_article;
                    if (currentId === articleId) {
                        state.currentArticle.visible = isVisible;
                        state.currentArticle.isVisible = isVisible;
                    }
                }
                state.error = null; // Clear error on success
            })
            .addCase(toggleArticleVisibility.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
    },
});

export const { clearError, resetStatus, clearCurrentArticle } = articleSlice.actions;

// Selectors
export const selectAllArticles = (state) => state.articles.articles;
export const selectCurrentArticle = (state) => state.articles.currentArticle;
export const selectArticleStatus = (state) => state.articles.status;
export const selectArticleError = (state) => state.articles.error;
export const selectUploadStatus = (state) => state.articles.uploadStatus;
export const selectUploadError = (state) => state.articles.uploadError;
export const selectUploadProgress = (state) => state.articles.uploadProgress;
export const selectUploads = (state) => state.articles.uploads;
export const selectUploadById = (state, id) => state.articles.uploads[id];

export default articleSlice.reducer;
